/**
 * Telefonda "asagi cek, birak, yenilensin" (kullanici, 08.10).
 *
 * Neden ozel bilesen: body'de `overscroll-behavior-y: none` var (Android'de
 * tarayicinin kendi yenilemesi kapali) ve ana ekrana eklenmis uygulamada (iOS
 * PWA, standalone) tarayici bu ozelligi hic sunmuyor. Yalnizca sayfa en ustteyken
 * ve dikey cekiste calisir; coin detayi (sheet) acikken ya da kendi icinde
 * kaydirilmis bir kutudan baslayan cekiste devreye girmez. Birakinca sayfa
 * yeniden yuklenir (en guvenilir "her sey taze" yolu).
 *
 * SES (kullanici secimi, 08.10: "hafif tik + pop"):
 *   birakma noktasina gelince - cok kisa, hafif "tik"
 *   esik asilip birakinca     - yumusak "pop" (inen kisa ton + minik cit)
 *   cekerken surekli ses YOK.
 * Dosya yok - Web Audio ile uretilir. iOS ses calmaya yalnizca "dokun-birak"
 * (touchend / click) sonrasi izin verir: ses her dokunusta acilir; sayfa
 * acildiktan sonra hic dokunmadan ilk cekiste "tik" olmayabilir, "pop"
 * (birakma = touchend) yine calar. iPhone sessiz modda ses yok; titresim
 * yalnizca Android (Apple web'e titresim izni vermiyor).
 */

import { useEffect, useRef, useState } from "react";

const THRESHOLD = 70; // gosterge piksel (parmak ~2 kati)
const MAX_PULL = 110;
const RESISTANCE = 0.5;

let audio: AudioContext | null = null;

function ctx(): AudioContext | null {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!audio && Ctx) audio = new Ctx();
    if (audio?.state === "suspended") void audio.resume();
    return audio;
  } catch {
    return null; // ses yoksa sessiz devam
  }
}

/** Kullanici hareketinde (touchend/click) sesi acar: iOS kilidini cozer. */
function primeAudio(): void {
  const a = ctx();
  if (!a) return;
  const src = a.createBufferSource();
  src.buffer = a.createBuffer(1, 1, 22_050);
  src.connect(a.destination);
  src.start(0);
}

/** Birakma noktasi: cok kisa, hafif "tik" (yalnizca ses zaten aciksa). */
function tick(): void {
  const a = audio;
  if (!a || a.state !== "running") return;
  const t = a.currentTime;
  const osc = a.createOscillator();
  const amp = a.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(2100, t);
  amp.gain.setValueAtTime(0.0001, t);
  amp.gain.exponentialRampToValueAtTime(0.05, t + 0.003);
  amp.gain.exponentialRampToValueAtTime(0.0001, t + 0.03);
  osc.connect(amp).connect(a.destination);
  osc.start(t);
  osc.stop(t + 0.04);
}

/** Birakinca: yumusak "pop" - hizla inen kisa ton + minik cit. */
function pop(): void {
  const a = ctx(); // touchend icinde: iOS'ta da calar
  if (!a) return;
  const t = a.currentTime + 0.005;
  const osc = a.createOscillator();
  const amp = a.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(620, t);
  osc.frequency.exponentialRampToValueAtTime(190, t + 0.07);
  amp.gain.setValueAtTime(0.0001, t);
  amp.gain.exponentialRampToValueAtTime(0.16, t + 0.006);
  amp.gain.exponentialRampToValueAtTime(0.0001, t + 0.11);
  osc.connect(amp).connect(a.destination);
  osc.start(t);
  osc.stop(t + 0.13);
  const len = Math.floor(a.sampleRate * 0.012);
  const buf = a.createBuffer(1, len, a.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const click = a.createBufferSource();
  click.buffer = buf;
  const hp = a.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.setValueAtTime(2500, t);
  const cAmp = a.createGain();
  cAmp.gain.setValueAtTime(0.05, t);
  click.connect(hp).connect(cAmp).connect(a.destination);
  click.start(t);
}

function startsInsideScrolled(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  while (el && el !== document.body) {
    if (el.closest(".sheet-backdrop, [role='dialog']")) return true;
    const style = window.getComputedStyle(el);
    if (/(auto|scroll)/.test(style.overflowY) && el.scrollTop > 0) return true;
    el = el.parentElement;
  }
  return false;
}

export function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const active = useRef(false);
  const pullRef = useRef(0);
  const readyRef = useRef(false);

  // Her dokun-birak / tiklamada sesi ac (iOS kilidi); cekise ozel degil.
  useEffect(() => {
    const opts = { passive: true, capture: true } as const;
    window.addEventListener("touchend", primeAudio, opts);
    window.addEventListener("click", primeAudio, opts);
    return () => {
      window.removeEventListener("touchend", primeAudio, opts);
      window.removeEventListener("click", primeAudio, opts);
    };
  }, []);

  useEffect(() => {
    if (!("ontouchstart" in window)) return undefined;
    const onStart = (e: TouchEvent) => {
      if (refreshing || e.touches.length !== 1 || window.scrollY > 0) return;
      if (startsInsideScrolled(e.target)) return;
      const t = e.touches[0]!;
      start.current = { x: t.clientX, y: t.clientY };
      active.current = false;
      readyRef.current = false;
    };
    const onMove = (e: TouchEvent) => {
      const s = start.current;
      if (!s) return;
      const t = e.touches[0]!;
      const dy = t.clientY - s.y;
      const dx = Math.abs(t.clientX - s.x);
      if (!active.current) {
        if (dy < 8 || dx > dy) {
          if (dx > 12 || dy < -4) start.current = null; // yatay kaydirma / yukari
          return;
        }
        active.current = true;
      }
      if (window.scrollY > 0) {
        start.current = null;
        pullRef.current = 0;
        setPull(0);
        return;
      }
      const next = Math.min(MAX_PULL, Math.max(0, dy * RESISTANCE));
      pullRef.current = next;
      setPull(next);
      const ready = next >= THRESHOLD;
      if (ready && !readyRef.current) {
        tick();
        navigator.vibrate?.(10);
      }
      readyRef.current = ready;
    };
    const onEnd = () => {
      if (!start.current) return;
      start.current = null;
      if (active.current && pullRef.current >= THRESHOLD) {
        pop();
        navigator.vibrate?.(18);
        setRefreshing(true);
        setPull(THRESHOLD);
        window.setTimeout(() => window.location.reload(), 300);
      } else {
        pullRef.current = 0;
        setPull(0);
      }
      active.current = false;
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [refreshing]);

  if (pull <= 0 && !refreshing) return null;
  const ready = pull >= THRESHOLD;
  return (
    <div
      className="ptr"
      data-ready={ready ? "true" : undefined}
      data-refreshing={refreshing ? "true" : undefined}
      style={{ transform: `translate(-50%, ${pull - 44}px)` }}
      role="status"
      aria-live="polite"
    >
      <svg
        className="ptr-icon"
        viewBox="0 0 24 24"
        width="20"
        height="20"
        aria-hidden="true"
        style={
          refreshing
            ? undefined
            : { transform: `rotate(${(pull / THRESHOLD) * 180}deg)` }
        }
      >
        <path
          d="M12 4v12m0 0-5-5m5 5 5-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="ptr-text">
        {refreshing
          ? "Yenileniyor…"
          : ready
            ? "Bırak, yenilensin"
            : "Yenilemek için çek"}
      </span>
    </div>
  );
}
